
import type { RouterConfig } from '@nuxt/schema'

// https://router.vuejs.org/api/interfaces/routeroptions.html
export default <RouterConfig>{
    routes: (_routes) => {
        // Only run on client-side to access window, or use process.server logic
        let host = ''
        if (process.server) {
            const { ssrContext } = useNuxtApp()
            // Try to get host from request headers
            host = ssrContext?.event?.node?.req?.headers?.host || ''
        } else {
            host = window.location.host
        }

        // Remove port number if present
        const domain = host.split(':')[0] || ''

        // Pages that must be reachable on EVERY subdomain regardless of the
        // subdomain-filter logic below.
        //   /account — fan balance + topup pages; user-account-scoped, not
        //     tenant-scoped, so they should be reachable wherever a logged-in
        //     user is browsing.
        //   /contact, /faq, /about — legal-adjacent info pages linked from
        //     AppFooter. The footer renders on every subdomain (shop, admin,
        //     creator, …) so the targets must too — otherwise customers on
        //     shop.* click "Contact" and hit a 404.
        //   /terms, /imprint, /datenschutz, /widerrufsrecht, /versand,
        //     /rueckgabe — German e-commerce legal pages (AGB, Impressum,
        //     privacy, withdrawal rights, shipping, returns). Required on the
        //     shop subdomain by EU consumer law; also linked from AppFooter
        //     across all subdomains (so /datenschutz must resolve everywhere
        //     too — otherwise the footer's Datenschutz link 404s on shop.*).
        //   /data-deletion — Meta/Facebook-mandated data-deletion instructions
        //     page. Linked from AppFooter and submitted to Meta as the app's
        //     "Data Deletion" URL, so — like /datenschutz — it must resolve on
        //     every subdomain (and the apex), not get swallowed by [...slug].
        const UNIVERSAL_PATHS = [
            '/account',
            '/contact',
            '/faq',
            '/about',
            '/terms',
            '/imprint',
            '/datenschutz',
            '/data-deletion',
            '/widerrufsrecht',
            '/versand',
            '/rueckgabe',
            // /forgot-password lives at the apex /pages/forgot-password.vue
            // and is linked from the shop's login page — without this entry
            // the link Vue-router-warns and 404s on shop.* subdomains.
            '/forgot-password',
            // Social login detour: when a provider returns no email address
            // (routine with Facebook), the OAuth callbacks redirect to
            // /register/finish ON THE ORIGINATING HOST to collect one — see
            // facebook.get.ts, soundcloud.get.ts and bridge.get.ts. Without
            // this entry every such signup dead-ends in a 404 on shop.*,
            // creator.* and every other subdomain, which is exactly where
            // those flows start.
            //
            // Deliberately the full path and NOT '/register': the bare
            // /register page is the plan-first creator signup and has no
            // business on the shop subdomain. The matcher below is
            // exact-or-prefix, so this entry admits /register/finish alone.
            '/register/finish'
        ]
        const isUniversal = (p: string) => {
            const stripped = p.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
            return UNIVERSAL_PATHS.some(u => stripped === u || stripped.startsWith(u + '/'))
        }

        // Two categories of non-universal staff paths:
        //
        // INTERNAL_PATHS — reachable on admin / creator / labels /
        // scouts AND shop, but NOT on the apex. Shop carries these as
        // discovery surfaces that work for both customers and staff
        // (the payment / download CTAs vary by viewer).
        //   /feed — Track Feed; discovery + stream/download stats.
        //     On shop.* the download CTA swaps to the standard shop
        //     cart (see feed.vue's `isShopContext` switch). On staff
        //     subdomains it offers the internal pay-what-you-want
        //     PayModal.
        //
        // STAFF_PATHS — reachable on admin / creator / labels /
        // scouts only. Not on shop, not on apex. Pure back-office.
        //   /team — internal artist roster browser. Gated to admin /
        //     creator / label_manager / ar via middleware/internal.ts.
        //     Off the apex so customer-facing URL space stays clean;
        //     off the shop because it has no discovery value for
        //     buyers and the middleware would 302 them anyway.
        const INTERNAL_PATHS = ['/feed']
        const STAFF_PATHS = ['/team']

        // NON_SHOP_PATHS — reachable everywhere EXCEPT the shop. Neither
        // UNIVERSAL (that would include the shop) nor STAFF (the apex
        // filter below actively excludes those), so this needs its own
        // category.
        //   /auth — the login page, and where every auth handler sends
        //     its error and "email confirmed" messages via
        //     resolveAuthLanding(). Those flows start on the apex AND on
        //     creator.* / admin.*, so it has to resolve on all of them.
        //     Off the shop on purpose: auth.vue's signup tab is hardcoded
        //     to role:'creator' and routes to creator.* on success, which
        //     is correct for staff and exactly the funnel mixing the
        //     customer/creator split exists to prevent. Shop visitors get
        //     the shop's own /login instead.
        const NON_SHOP_PATHS = ['/auth']
        const isNonShop = (p: string) => {
            const stripped = p.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
            return NON_SHOP_PATHS.some(u => stripped === u || stripped.startsWith(u + '/'))
        }
        const isInternal = (p: string) => {
            const stripped = p.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
            return INTERNAL_PATHS.some(u => stripped === u || stripped.startsWith(u + '/'))
        }
        const isStaff = (p: string) => {
            const stripped = p.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
            return STAFF_PATHS.some(u => stripped === u || stripped.startsWith(u + '/'))
        }

        // If we are on an admin subdomain (e.g. admin.localhost, admin.lvh.me, admin.tenant.development)
        if (domain.startsWith('admin.')) {
            const adminRoutes = _routes
                .filter(r => r.path.includes('/admin') || isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path))
                .map(r => {
                    if (isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path)) return r
                    // Adjust path: /admin -> /, /de/admin -> /de, /admin/users -> /users
                    // For /admin/accounting/distributor -> /accounting/distributor
                    let newPath = r.path.replace('/admin', '')
                    if (newPath === '') newPath = '/'
                    // Ensure it starts with / if not empty
                    if (!newPath.startsWith('/')) newPath = '/' + newPath

                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `admin-${String(r.name)}` : undefined
                    }
                })
            return adminRoutes
        }

        // If we are on a creator subdomain (e.g. creator.localhost, creator.lvh.me, creator.tenant.development)
        if (domain.startsWith('creator.')) {
            const creatorRoutes = _routes
                .filter(r => r.path.includes('/creator') || isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path))
                .map(r => {
                    if (isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path)) return r
                    // Adjust path: /creator -> /, /de/creator -> /de, /creator/add -> /add
                    let newPath = r.path.replace('/creator', '')
                    if (newPath === '') newPath = '/'

                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `creator-${String(r.name)}` : undefined
                    }
                })
            return creatorRoutes
        }

        // labels.* subdomain — Label Manager panel. Two page trees feed
        // into this subdomain:
        //   1. /pages/labels/*  — LM-specific surfaces that diverged from
        //                          the creator panel (Finance hub, etc.).
        //                          Stripped of the `/labels` prefix.
        //   2. /pages/creator/* — everything else (distribution, releases,
        //                          shop, tooling). Stripped of `/creator`.
        // When both trees produce the same final path, the /labels/*
        // version wins so the creator-only personal finance page never
        // bleeds onto labels.*. We resolve the collision by collecting
        // labels-derived final paths first and dropping any creator-
        // derived route that would overwrite one of them.
        if (domain.startsWith('labels.')) {
            // `/labels/...` only matches the *root-level* labels page
            // tree — NOT `/creator/distribution/labels/...` (which is
            // the label catalogue under the shared distribution panel).
            // The locale-prefix regex `/(en|de|pt|es)/labels` covers Nuxt's
            // i18n routes. Anchoring at start avoids the catch-all
            // `.includes('/labels')` matching `/distribution/labels` and
            // mangling its path on the labels.* subdomain.
            const LABELS_ROOT_RE = /^\/(?:(?:en|de|pt|es)\/)?labels(?:\/|$)/
            const labelsOnly = _routes
                .filter(r => LABELS_ROOT_RE.test(r.path))
                .map(r => {
                    let newPath = r.path.replace('/labels', '')
                    if (newPath === '') newPath = '/'
                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `labels-${String(r.name)}` : undefined
                    }
                })
            const labelsPathSet = new Set(labelsOnly.map(r => r.path))

            const creatorDerived = _routes
                .filter(r =>
                    (r.path.includes('/creator') || isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path))
                    && !LABELS_ROOT_RE.test(r.path))
                .map(r => {
                    if (isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path)) return r
                    let newPath = r.path.replace('/creator', '')
                    if (newPath === '') newPath = '/'
                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `labels-${String(r.name)}` : undefined
                    }
                })
                // Drop any creator-derived route that collides with a
                // labels-derived one — labels wins, so creator.* surfaces
                // never bleed onto the LM panel.
                .filter(r => !labelsPathSet.has(r.path))

            return [...labelsOnly, ...creatorDerived]
        }

        // If we are on a shop subdomain (e.g. shop.localhost, shop.lvh.me, shop.tenant.development)
        // Shop is the customer-facing storefront. The Track Feed is
        // ALSO available here as a discovery surface (matching internal
        // subdomains), but the download CTA on the shop swaps the
        // pay-what-you-want PayModal for the regular shop cart at
        // standard prices. See feed.vue's `isShopContext` switch.
        if (domain.startsWith('shop.')) {
            const shopRoutes = _routes
                .filter(r => r.path.includes('/shop') || isUniversal(r.path) || isInternal(r.path))
                .map(r => {
                    if (isUniversal(r.path) || isInternal(r.path)) return r
                    let newPath = r.path.replace('/shop', '')
                    if (newPath === '') newPath = '/'

                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `shop-${String(r.name)}` : undefined
                    }
                })
            return shopRoutes
        }

        // scouts.* subdomain — A&R scout panel. Page tree lives under
        // /scouts/* in the source; we strip that prefix so the scout
        // sees clean URLs (scouts.{tenant}/profile, /roster, etc.)
        // matching the labels.* / creator.* convention.
        if (domain.startsWith('scouts.')) {
            const scoutsRoutes = _routes
                .filter(r => r.path.includes('/scouts') || isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path))
                .map(r => {
                    if (isUniversal(r.path) || isInternal(r.path) || isStaff(r.path) || isNonShop(r.path)) return r
                    let newPath = r.path.replace('/scouts', '')
                    if (newPath === '') newPath = '/'

                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `scouts-${String(r.name)}` : undefined
                    }
                })
            return scoutsRoutes
        }

        // superadmin.* subdomain — platform Super Admin panel. The page
        // tree lives under /pages/superadmin/* in source; strip that prefix
        // so the panel sits at the ROOT of the subdomain
        // (superadmin.{host}/tenants, /users, /health, …) matching the
        // admin.* / creator.* convention — the /superadmin path prefix is
        // redundant once you're already on the superadmin subdomain. Keep
        // /auth (the unauthenticated login bounce), /feed (the cross-tenant
        // Track Feed) and the universal pages reachable here too.
        if (domain.startsWith('superadmin.')) {
            const isAuthPath = (p: string) => {
                const s = p.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
                return s === '/auth' || s.startsWith('/auth/')
            }
            return _routes
                .filter(r => r.path.includes('/superadmin') || isUniversal(r.path) || isInternal(r.path) || isAuthPath(r.path))
                .map(r => {
                    if (isUniversal(r.path) || isInternal(r.path) || isAuthPath(r.path)) return r
                    // /superadmin -> /, /de/superadmin -> /de, /superadmin/tenants -> /tenants
                    let newPath = r.path.replace('/superadmin', '')
                    if (newPath === '') newPath = '/'
                    if (!newPath.startsWith('/')) newPath = '/' + newPath
                    return {
                        ...r,
                        path: newPath,
                        name: r.name ? `superadmin-${String(r.name)}` : undefined
                    }
                })
        }

        // Apex (no subdomain) — customer-facing main site. Exclude
        // every staff-only path tree (admin/creator/shop/labels), the
        // internal paths (feed), and the staff-only paths (team).
        // /scouts/* stays for legacy invite-email landing; the
        // scouts-redirect middleware bounces logged-in scouts to
        // scouts.{tenant} and the page-level middleware kicks anonymous
        // visitors to /auth.
        const APEX_LABELS_ROOT_RE = /^\/(?:(?:en|de|pt|es)\/)?labels(?:\/|$)/
        return _routes.filter(r =>
            !r.path.includes('/admin')
            && !r.path.includes('/superadmin')
            && !r.path.includes('/creator')
            && !r.path.includes('/shop')
            && !APEX_LABELS_ROOT_RE.test(r.path)
            && !isInternal(r.path)
            && !isStaff(r.path)
        )
    }
}
